import React from 'react'

import { useTranslation } from 'language/translator'

import { Sort } from 'utils/types'

import Box from 'components_new/atoms/Box'
import LinearProgress from 'components_new/atoms/LinearProgress'
import LoadingSpinner from 'components_new/atoms/LoadingSpinner'
import Text from 'components_new/atoms/Text'

import SegmentVirtualizedTable from 'components_new/molecules/SegmentVirtualizedTable'

import { isInitialSort, TRowsStatus, TSelectedParsedData } from '../utils'

import Loading from './loading'

export interface TableViewProps {
  // only the first rows were fetched; more match the selection
  hasMore: boolean
  onLinkClick: () => void
  onSearch: (columnId: string, query: string) => void
  onSort: (sort: Sort) => void
  parsedData: TSelectedParsedData
  // the rows are the first ones in the table's sort across all rows
  sortedOnServer: boolean
  status: TRowsStatus
}

const TableView = (props: TableViewProps) => {
  const {
    hasMore,
    onLinkClick,
    onSearch,
    onSort,
    parsedData,
    sortedOnServer,
    status
  } = props

  const { t } = useTranslation()

  // a search or sort runs over all rows on the server
  const refreshing = status === 'search' || status === 'sort'

  const shownRows = parsedData.rows.length
  const isSearched = shownRows !== parsedData.totalRows
  // A search the server ran leaves nothing to filter out here.
  const hasSearch = Object.values(parsedData.search).some(
    (query) => query.trim().length > 0
  )

  // A capped table sorted on a column the server can't sort by (the metric's
  // value) is sorted among the fetched rows only.
  const sortsFetchedRowsOnly
    = hasMore && !sortedOnServer && !isInitialSort(parsedData.sort)

  const getCountCaption = () => {
    if (isSearched) {
      return t(
        'global',
        hasMore ? 'showing_rows_of_first' : 'showing_rows',
        false,
        { count: String(shownRows), total: String(parsedData.totalRows) }
      )
    }

    if (hasSearch) {
      return t(
        'global',
        hasMore ? 'first_matching_rows_only' : 'matching_rows',
        false,
        { count: String(shownRows) }
      )
    }

    return t('global', hasMore ? 'first_rows_only' : 'total_rows', false, {
      count: String(shownRows)
    })
  }

  const getRowsCaption = () => {
    if (refreshing) {
      return t(
        'global',
        status === 'search' ? 'searching_all_rows' : 'sorting_all_rows'
      )
    }

    if (status === 'refresh_failed') {
      return t('global', 'rows_failed_to_refresh')
    }

    const caption = getCountCaption()

    return sortsFetchedRowsOnly
      ? `${caption} ${t('global', 'sorted_among_fetched_rows')}`
      : caption
  }

  if (status === 'loading') {
    return <Loading sx={{ flex: 1, overflow: 'hidden', p: 2 }} />
  }

  if (status === 'failed') {
    return (
      <Box sx={{ flex: 1, p: 2 }}>
        <Text color="text.secondary" variant="body2">
          {t('global', 'rows_failed_to_load')}
        </Text>
      </Box>
    )
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {refreshing ? (
          <LinearProgress
            sx={{
              height: 3,
              left: 0,
              position: 'absolute',
              right: 0,
              top: 0,
              zIndex: 3
            }}
            variant="indeterminate"
          />
        ) : null}
        <SegmentVirtualizedTable
          columns={parsedData.columns}
          data={parsedData.rows}
          loading={refreshing}
          onLinkClick={onLinkClick}
          onSearch={onSearch}
          onSort={onSort}
          search={parsedData.search}
          sort={parsedData.sort}
          sx={{
            borderRadius: 1,
            flex: 1,
            // the rows shown are the old ones until the server's arrive
            opacity: refreshing ? 0.5 : 1,
            overflow: 'hidden',
            transition: 'opacity 150ms'
          }}
        />
      </Box>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, px: 2, py: 1 }}>
        {refreshing ? <LoadingSpinner size={12} /> : null}
        <Text color="text.secondary" variant="caption">
          {getRowsCaption()}
        </Text>
      </Box>
    </>
  )
}

export default TableView
